import { ComponentLibrary } from '@suntex.classic/adobe-animate';
import {
  type PropertyPath,
  type TargettedEvent,
  UIUtil,
} from '@suntex.core/animation';
import { $Color, Color } from '@suntex.core/color';
import { LogLevel, Logger } from '@suntex.core/logging';
import { JsonObject } from '@suntex.core/types';
import { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';

import { ModuleSlug, NodeModel } from '../types';
import { resolveConstruction } from '../utils/resolveConstruction';

import { Button, ButtonIcon } from './Button';
import { ImageIcon } from './ImageIcon';
import type { InfoPane } from './InfoPane';
import { Label } from './Label';
import { Stem } from './Stem';
import { TextLabel } from './TextLabel';
import { BUTTON_RADIUS } from './const';
import type { IHub, IRadial } from './types';

const ROTATION_EASE = 'easeInOut';
const ELASTIC_EASE = 'elasticOut';
const BASE_ANIMATION_MS = 100;
const ALPHA_ANIMATION_FACTOR = 0.5;
const ELASTIC_ANIMATION_FACTOR = 8;
const RESET_SCALE_ANIMATION_FACTOR = 2;
const EMERGE_STEM_LENGTH_FACTOR = 0.35;
const EMERGE_STEM_WIDTH_FACTOR = 3;
const EMERGE_ROTATION_OFFSET = -45;
const EMERGE_ROTATION_ANIMATION_SCALE_FACTOR = 2;
const SHOWN_ALPHA = 1.0;
const HIDDEN_ALPHA = 0.0;
const STEM_ALPHA = 0.35;
const STEM_COLLAPSED_WIDTH_FACTOR = 2;

const BREADCRUMB_SPACING = 50;
const BREADCRUMB_SCALE = 0.25;
const KEYBOARD_FOCUS_SCALE_FACTOR = 1.1;
// Enlarges the radius of the top breadcrumb by SOLE_BREADCRUMB_SCALE_FACTOR
// when it is the only breadcrumb displayed (i.e. the central radial is its
// direct child).
const SOLE_BREADCRUMB_SCALE_FACTOR = 1.5;

// Emphasis
const EMPHASIZED_STEM_WIDTH_FACTOR = 4;
const EMPHASIZED_RADIUS_FACTOR = 1.1;
const EMPHASIZED_STEM_ALPHA = 0.5;

// Wobble
const ANIMATE_WOBBLE = false;
const WOBBLE_ANIMATION_MIN_MS = 3000;
const WOBBLE_DRIFT_X_RANGE = 8;
const WOBBLE_DRIFT_X_OFFSET = 4;
const WOBBLE_DRIFT_ANGLE_RANGE = 6;
const WOBBLE_DRIFT_ANGLE_OFFSET = 3;

const logger = Logger.for(() => Radial, { level: LogLevel.Debug });

export const RADIAL_KEYBOARD_BLUR_EVENT = 'radialkeyboardblur';
export const RADIAL_KEYBOARD_FOCUS_EVENT = 'radialkeyboardfocus';

type RadialPosition = 'center' | 'moving-to-child' | 'child' | 'breadcrumb';
export type ActivationEvent = TargettedEvent<Button> & {
  nativeEvent?: Event;
};

/* Radial */
export class Radial extends createjs.Container implements IRadial {
  // config
  public readonly key: string;
  public readonly angle: number; // rot
  public readonly stemLength: number;
  public readonly size: number;
  public readonly color: Color;
  public readonly href?: string;
  public readonly tag?: string;
  public readonly ariaLabel: string;
  public readonly hidden: boolean;
  public readonly alias?: string[];
  public readonly stemColor?: Color;
  public readonly moduleSlug?: ModuleSlug;
  public readonly dataPath?: PropertyPath<Entities_Player_ProgressSummary_Response>;
  public readonly dataTarget?: number;
  private readonly hideProgressBar?: boolean;
  private readonly recursiveLogic: 'OR' | 'AND';

  // state
  public display: boolean = true;
  public moveX: number = 0;
  public moveY: number = 0;
  private _emphasized: boolean = false;
  private _enabled: boolean = true;
  private _inFocus: boolean = true;
  private _progress: number = 0;
  // Once a radial is marked under construction it stays disabled and star-free
  // for the life of the hub, regardless of later enabled/focus/progress passes.
  private _underConstruction: boolean = false;
  private position: RadialPosition | null = null;
  private _targetPosition: { x: number; y: number } | null = null;
  // Whether this radial is the sole breadcrumb (top breadcrumb with no others
  // displayed, i.e. the central radial is its direct child). Only meaningful
  // while position === 'breadcrumb'; see computeRestingScale().
  private _isSoleBreadcrumb: boolean = false;

  // structure
  public button: Button;
  public stem: Stem;
  public icon: ButtonIcon;
  public radialParent: Radial | null = null;
  public radialChildren: Radial[] = [];
  public redirect: Radial | null = null;
  public infoPane: InfoPane | null = null;
  private _hub: IHub | null = null;

  private mouseOverFn = () => this.onHover();
  private mouseOutFn = () => this.onLeave();
  private keyboardFocusFn = () => this.showKeyboardFocus();
  private keyboardBlurFn = () => this.hideKeyboardFocus();
  private clickFn: ((event: TargettedEvent<Button>) => void) | null = null;

  get emphasized(): boolean {
    return this._emphasized && this.enabled && this.inFocus;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  get inFocus(): boolean {
    return this._inFocus;
  }

  get hub(): IHub {
    if (!this._hub) throw new Error('Hub not initialized');
    return this._hub;
  }

  set hub(hub: IHub) {
    this._hub = hub;
    for (const radial of this.radialChildren) {
      radial.hub = hub;
    }
  }

  get radialAncestors(): Radial[] {
    const ancestors: Radial[] = [];
    let radial: Radial | null = this.radialParent;

    while (radial) {
      ancestors.unshift(radial);
      radial = radial.radialParent;
    }
    return ancestors;
  }

  get radialDescendents(): Radial[] {
    const descendents: Radial[] = [];
    for (const radial of this.radialChildren) {
      descendents.push(radial);
      descendents.push(...radial.radialDescendents);
    }
    return descendents;
  }

  public getStarStats(): {
    goldStars: number;
    silverStars: number;
    totalStars: number;
    currentProgress: number;
  } {
    let goldStars = 0;
    let silverStars = 0;
    let totalStars = 0;

    function countStars(radial: Radial) {
      if (radial.inFocus) {
        const progress = radial._progress;
        const starValue = Math.floor(progress);

        if (starValue >= 2) {
          goldStars++;
          silverStars++;
        } else if (starValue >= 1) {
          silverStars++;
        }
        totalStars++;
      }

      for (const child of radial.radialChildren) {
        countStars(child);
      }
    }

    for (const child of this.radialChildren) {
      countStars(child);
    }

    return {
      goldStars,
      silverStars,
      totalStars,
      currentProgress: this._progress,
    };
  }

  private get animationMs(): number {
    return this.hub.animationDurationFactor * BASE_ANIMATION_MS;
  }

  private get traceData() {
    return {
      tag: this.tag,
      moduleSlug: this.moduleSlug,
      position: this.position,
    };
  }

  constructor(
    readonly lib: ComponentLibrary,
    rot: number,
    stemLength: number,
    size: number,
    color: number,
    icon: ButtonIcon,
    tag: string | null,
    href: string | null,
    stemColor: number,
    moduleSlug: ModuleSlug | null,
    opts: {
      key: string;
      ariaLabel: string;
      alias?: string[];
      dataPath?: PropertyPath<Entities_Player_ProgressSummary_Response>;
      dataTarget?: number;
      hidden?: boolean;
      hideProgressBar?: boolean;
      recursiveLogic?: 'OR' | 'AND';
    },
  ) {
    super();
    this.angle = rot;
    this.stemLength = stemLength;
    this.size = size;
    this.color = $Color.from(color);
    this.tag = tag ?? undefined;
    this.key = opts.key;
    this.ariaLabel = opts.ariaLabel;
    this.hidden = opts.hidden ?? false;
    this.href = href ?? undefined;
    this.moduleSlug = moduleSlug ?? undefined;
    this.stemColor = $Color.from(stemColor);
    this.alias = opts.alias;
    this.dataPath = opts.dataPath;
    this.dataTarget = opts.dataTarget;
    this.hideProgressBar = opts.hideProgressBar;
    this.recursiveLogic = opts.recursiveLogic ?? 'OR';

    this.stem = new Stem(stemLength, this.stemColor);
    this.stem.alpha = 0.0;

    this.radialChildren = [];
    this.radialParent = null;

    this.icon = icon;
    this.button = new Button(lib, size, this.color, icon);
    this.addChild(this.stem);
    this.addChild(this.button);

    this.button.mouseChildren = false; // Added 02/01/2019. This prevents icon from capturing events.

    UIUtil.onMouseOver(this.button, this.mouseOverFn);
    UIUtil.onMouseOut(this.button, this.mouseOutFn);
    this.button.addEventListener(
      RADIAL_KEYBOARD_FOCUS_EVENT,
      this.keyboardFocusFn,
    );
    this.button.addEventListener(
      RADIAL_KEYBOARD_BLUR_EVENT,
      this.keyboardBlurFn,
    );
    this.clickFn = (event) => this.onClick(event);
    UIUtil.onClick(this.button, this.clickFn);

    this.button.back.alpha = HIDDEN_ALPHA;

    UIUtil.useHandCursor(this.button);

    const animateWobble = () => {
      const animationMs =
        WOBBLE_ANIMATION_MIN_MS + WOBBLE_ANIMATION_MIN_MS * Math.random();
      const animationFactor = this._hub?.animationDurationFactor ?? 0;

      if (animationFactor && this.position === 'child') {
        const driftX =
          Math.random() * WOBBLE_DRIFT_X_RANGE - WOBBLE_DRIFT_X_OFFSET;
        const driftAngle =
          Math.random() * WOBBLE_DRIFT_ANGLE_RANGE - WOBBLE_DRIFT_ANGLE_OFFSET;
        UIUtil.tween(
          this.button,
          'x',
          -this.stemLength + driftX,
          animationMs,
          'easeInOut',
        );
        UIUtil.tween(
          this,
          'rotation',
          this.angle + driftAngle,
          animationMs,
          'easeInOut',
        );
      }
      setTimeout(animateWobble, animationMs);
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ANIMATE_WOBBLE is a feature flag that may be changed
    if (ANIMATE_WOBBLE) {
      animateWobble();
    }
  }

  /**
   * Cleanup method to remove event listeners and prevent phantom interactions.
   * Call this before removing the radial from the stage.
   */
  public destroy(): void {
    // Remove event listeners from button
    this.button.removeEventListener('mouseover', this.mouseOverFn);
    this.button.removeEventListener('mouseout', this.mouseOutFn);
    this.button.removeEventListener(
      RADIAL_KEYBOARD_FOCUS_EVENT,
      this.keyboardFocusFn,
    );
    this.button.removeEventListener(
      RADIAL_KEYBOARD_BLUR_EVENT,
      this.keyboardBlurFn,
    );
    if (this.clickFn) {
      this.button.removeEventListener('click', this.clickFn);
    }

    // Recursively destroy children
    for (const child of this.radialChildren) {
      child.destroy();
    }
  }

  public toJSON(): JsonObject {
    return {
      tag: this.tag,
      moduleSlug: this.moduleSlug,
      ariaLabel: this.ariaLabel,
      hidden: this.hidden,
      position: this.position,
      children: this.radialChildren.map((c) => c.toJSON()),
    };
  }

  public addRadial(params: {
    angle: number;
    stemLength: number;
    size: number;
    color?: number;
    icon: ButtonIcon;
    tag?: string | null;
    key: string;
    alias?: string[];
    ariaLabel: string;
    href?: string | null;
    moduleSlug?: ModuleSlug;
    dataPath?: PropertyPath<Entities_Player_ProgressSummary_Response>;
    dataTarget?: number;
    hidden?: boolean;
    hideProgressBar?: boolean;
    recursiveLogic?: 'OR' | 'AND';
  }): Radial {
    const radial = new Radial(
      this.lib,
      params.angle,
      params.stemLength,
      params.size,
      params.color ?? this.color,
      params.icon,
      params.tag ?? null,
      params.href ?? null,
      this.color,
      params.moduleSlug ?? null,
      params,
    );
    radial.radialParent = this;
    this.radialChildren.push(radial);
    return radial;
  }

  public getRadialChild(
    model: Pick<NodeModel, 'tag' | 'moduleSlug'>,
  ): Radial | null {
    for (const r of this.radialChildren) {
      if (
        (r.tag && r.tag === model.tag) ||
        (r.alias && model.tag && r.alias.includes(model.tag)) ||
        (r.moduleSlug && r.moduleSlug === model.moduleSlug)
      ) {
        return r;
      }
    }
    return null;
  }

  public getDescendent(path: string[]): Radial | null {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let radial: Radial | null = this;

    for (const tag of path) {
      if (tag != '') {
        radial = radial.getRadialChild({ tag });
        if (radial == null) break;
      }
    }

    return radial;
  }

  public getFirstDescendent(
    model: Pick<NodeModel, 'tag' | 'moduleSlug'>,
  ): Radial | null {
    let radial = this.getRadialChild(model);

    if (radial) {
      return radial;
    }

    for (const child of this.radialChildren) {
      radial = child.getFirstDescendent(model);
      if (radial) {
        return radial;
      }
    }

    return null;
  }

  public getDescendentOrSelf(path: string[]): Radial {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let radial: Radial = this;

    for (const tag of path) {
      if (tag != '') {
        const nextRadial = radial.getRadialChild({ tag });
        if (nextRadial === null) break;
        radial = nextRadial;
      }
    }

    return radial;
  }

  public checkEnabled(isEnabled: (moduleSlug: ModuleSlug) => boolean): boolean {
    return this.checkProperty(
      isEnabled,
      (radial) => radial.checkEnabled(isEnabled),
      (result) => this.setEnabled(result),
    );
  }

  public checkInFocus(isInFocus: (moduleSlug: ModuleSlug) => boolean): boolean {
    return this.checkProperty(
      isInFocus,
      (radial) => radial.checkInFocus(isInFocus),
      (result) => this.setInFocus(result),
    );
  }

  private checkProperty(
    predicate: (moduleSlug: ModuleSlug) => boolean,
    recursiveCheck: (radial: Radial) => boolean,
    setSetter: (result: boolean) => void,
  ): boolean {
    let result = true;

    if (this.moduleSlug) {
      result = predicate(this.moduleSlug);
    } else if (this.radialChildren.length > 0) {
      result = this.recursiveLogic === 'AND'; // Start with true for AND, false for OR
      for (const radial of this.radialChildren) {
        if (radial.radialParent === this) {
          const childResult = recursiveCheck(radial);
          if (this.recursiveLogic === 'OR') {
            if (childResult) {
              result = true;
            }
          } else {
            // AND logic
            if (!childResult) {
              result = false;
            }
          }
        }
      }
    }

    setSetter(result);
    return result;
  }

  public setEmphasized(emphasized: boolean): void {
    this._emphasized = emphasized;
    this.button.setEmphasized(emphasized);

    // Update stem appearance if radial is in child position
    if (this.position === 'child') {
      this.stem.alpha = emphasized ? EMPHASIZED_STEM_ALPHA : STEM_ALPHA;
      this.stem.scaleY = emphasized ? EMPHASIZED_STEM_WIDTH_FACTOR : 1;
      this.button.x =
        -this.stemLength * (emphasized ? EMPHASIZED_RADIUS_FACTOR : 1);
    }
  }

  /**
   * The single source of truth for this radial's resting (non-hover) button
   * scale, based on its current position. Center and breadcrumb positions
   * have fixed resting scales; all other positions (child, moving-to-child,
   * or none yet) fall back to the button's own enabled/focus/emphasis-based
   * scale.
   */
  private computeRestingScale(): number {
    switch (this.position) {
      case 'center':
        return 1;
      case 'breadcrumb':
        return (
          BREADCRUMB_SCALE *
          (this._isSoleBreadcrumb ? SOLE_BREADCRUMB_SCALE_FACTOR : 1)
        );
      default:
        return this.button.restingChildScale;
    }
  }

  private applyRestingScale(animationMs: number): void {
    this.button.setScale(this.computeRestingScale(), animationMs);
  }

  private updateVisualState(): void {
    // An under-construction radial stays non-interactive regardless of later
    // enabled/focus passes.
    this.mouseEnabled = this._enabled && !this._underConstruction;
    this.stem.visible = this._enabled && this._inFocus;

    // Update render/label state only here; scale is always reapplied via
    // computeRestingScale() below so center/breadcrumb scaling (including the
    // sole-breadcrumb enlargement) isn't lost on defensive enabled/focus refreshes.
    this.button.setVisualState(this._enabled, this._inFocus);
    this.applyRestingScale(0);

    if (this.position !== 'moving-to-child') {
      this.animateShow(0);
    }
  }

  public setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this.updateVisualState();
  }

  public setInFocus(inFocus: boolean): void {
    this._inFocus = inFocus;
    this.updateVisualState();
    // Reapply progress to update star visibility based on new focus state
    this.showProgress(this._progress);
  }

  public setDisplaySettings({
    flatten,
    alwaysShowLabels,
  }: {
    flatten?: boolean;
    alwaysShowLabels?: boolean;
  }): void {
    this.button.flatten = !!flatten;
    this.button.alwaysShowLabels = !!alwaysShowLabels;
  }

  public getFullPath(): string {
    return this.key === '' ? '/' : this.key;
  }

  /**
   * Finds the descendant (or self) whose stored `key` exactly matches the
   * given path. Keys are unique across the whole tree (including filtered
   * views resolved via resolveFilterPath), unlike tag-based lookups which
   * can collide when the same tag/moduleSlug appears in multiple branches.
   */
  public getByKey(key: string): Radial | null {
    if (this.key === key) return this;
    for (const child of this.radialChildren) {
      const found = child.getByKey(key);
      if (found) return found;
    }
    return null;
  }

  public construction(): void {
    this._underConstruction = true;
    UIUtil.disable(this.button);
    this.button.setIcon(new ImageIcon('under-construction'));
    // Progress may already have been applied before construction runs; clear
    // any stars so an offline node doesn't advertise progress.
    this.button.unstar();
  }

  public get underConstruction(): boolean {
    return this._underConstruction;
  }

  /**
   * Walks the tree marking radials as under construction. A module leaf is
   * under construction when the predicate matches its slug. A container is
   * under construction only when it has at least one module-leaf descendant and
   * every such descendant is under construction (AND-propagation with a
   * has-descendant guard, so empty/structural containers are never marked).
   *
   * @returns whether this radial is under construction.
   */
  public checkConstruction(
    isUnderConstruction: (moduleSlug: ModuleSlug) => boolean,
  ): boolean {
    return resolveConstruction<Radial>(
      this,
      (radial) => radial.moduleSlug,
      (radial) =>
        radial.radialChildren.filter((child) => child.radialParent === radial),
      isUnderConstruction,
      (radial, result) => {
        if (result.underConstruction) radial.construction();
      },
    ).underConstruction;
  }

  /**
   * @param progress a number from 0.0 to 2.0, where 0.0 is no progress, 1.0 is silver star, and 2.0 is a gold star
   */
  public showProgress(progress: number): void {
    this._progress = progress;
    if (
      this._underConstruction ||
      !this.inFocus ||
      progress === 0 ||
      (this.hideProgressBar && progress < 1)
    ) {
      this.button.unstar();
    } else {
      const STAR_FRAMES_COUNT = 12;
      this.button.star(Math.floor(progress * STAR_FRAMES_COUNT));
    }
  }

  public setPosition(moveX: number = 0, moveY: number = 0): void {
    this.moveX = moveX;
    this.moveY = moveY;
  }

  public appearInCenter({
    animationMs,
    offsetX,
    offsetY,
  }: {
    animationMs: number;
    offsetX: number;
    offsetY: number;
  }): void {
    logger.forMethod(this.appearInCenter).trace(this.traceData);

    this.position = 'center';
    this._isSoleBreadcrumb = false;
    this.x = offsetX;
    this.y = offsetY;
    this.rotation = 0;
    this.alpha = HIDDEN_ALPHA;
    const button = this.button;
    button.x = 0;
    button.rotation = 0;
    button.scale = 1;
    button.back.alpha = SHOWN_ALPHA;
    this.stem.scaleX = 0;
    this.stem.scaleY = STEM_COLLAPSED_WIDTH_FACTOR;
    this.stem.alpha = HIDDEN_ALPHA;
    this.animateShow(animationMs);
    this.animateCollapse(animationMs);
  }

  public emergeFromCenter({
    animationMs,
    delayMs,
    offsetX,
    offsetY,
  }: {
    animationMs: number;
    delayMs: number;
    offsetX: number;
    offsetY: number;
  }): void {
    const l = logger.forMethod(this.emergeFromCenter);
    l.trace(this.traceData);

    this.position = 'moving-to-child';
    const button = this.button;

    this.x = offsetX;
    this.y = offsetY;

    this._isSoleBreadcrumb = false;
    this.applyRestingScale(animationMs);
    button.back.alpha = HIDDEN_ALPHA;

    this.alpha = HIDDEN_ALPHA;
    this.stem.alpha = STEM_ALPHA;

    this.rotation = this.angle + EMERGE_ROTATION_OFFSET;
    this.stem.scaleX = EMERGE_STEM_LENGTH_FACTOR; // length
    this.stem.scaleY = // width
      EMERGE_STEM_WIDTH_FACTOR *
      (this.emphasized ? EMPHASIZED_STEM_WIDTH_FACTOR : 1);
    button.rotation = -(this.angle + EMERGE_ROTATION_OFFSET);
    button.x = -this.stemLength * EMERGE_STEM_LENGTH_FACTOR;

    UIUtil.withDelay(
      () => {
        l.trace({
          tag: this.tag,
          animationMs,
          delayMs,
        });
        this.alpha = HIDDEN_ALPHA;
        this.animateShow(animationMs);
        this.animateExpand(animationMs);
        this.animatePosition({ x: offsetX, y: offsetY }, animationMs);
        UIUtil.withDelay(
          () => {
            this.position = 'child';
          },
          { ms: animationMs, scope: this.hub.delayScope },
        )();
      },
      { ms: delayMs, scope: this.hub.delayScope },
    )();
  }

  public moveToCenter({
    animationMs,
    offsetX,
    offsetY,
  }: {
    animationMs: number;
    offsetX: number;
    offsetY: number;
  }): void {
    logger.forMethod(this.moveToCenter).trace(this.traceData);

    this.position = 'center';
    this._isSoleBreadcrumb = false;
    const button = this.button;

    this.animateShow(animationMs);
    this.stem.alpha = HIDDEN_ALPHA;
    this.animateCollapse(animationMs);
    this.animatePosition({ x: offsetX, y: offsetY }, animationMs);
    this.applyRestingScale(animationMs);
    button.back.alpha = SHOWN_ALPHA;
  }

  public moveFromCenter({
    animationMs,
    offsetX,
    offsetY,
  }: {
    animationMs: number;
    offsetX: number;
    offsetY: number;
  }): void {
    logger.forMethod(this.moveFromCenter).trace(this.traceData);

    this.position = 'moving-to-child';
    this._isSoleBreadcrumb = false;
    const button = this.button;

    this.stem.alpha = STEM_ALPHA;
    this.rotation = this.angle;
    button.rotation = -this.angle;
    this.applyRestingScale(animationMs * RESET_SCALE_ANIMATION_FACTOR);
    button.back.alpha = HIDDEN_ALPHA;

    this.animateShow(animationMs);
    this.animateExpand(animationMs);
    this.animatePosition({ x: offsetX, y: offsetY }, animationMs);
    UIUtil.withDelay(
      () => {
        this.position = 'child';
      },
      { ms: animationMs, scope: this.hub.delayScope },
    )();
  }

  public moveToBreadcrumb({
    depth,
    animationMs,
    isSole = false,
  }: {
    depth: number;
    animationMs: number;
    isSole?: boolean;
  }): void {
    logger.forMethod(this.moveToBreadcrumb).trace(this.traceData);

    this.onLeave();
    this.position = 'breadcrumb';
    this._isSoleBreadcrumb = isSole;

    // The sole breadcrumb is enlarged (see SOLE_BREADCRUMB_SCALE_FACTOR), but its
    // button registration point is centered, so simply enlarging it in place would
    // clip it against the top/left stage edges. Shift it right and down by the
    // extra pixel radius the enlargement introduces to keep it fully on-stage.
    const soleBreadcrumbOffset = isSole
      ? BUTTON_RADIUS * BREADCRUMB_SCALE * (SOLE_BREADCRUMB_SCALE_FACTOR - 1)
      : 0;

    this.animateShow(animationMs);
    this.animateCollapse(animationMs);
    this.animatePosition(
      {
        x: -this.hub.centerX + BREADCRUMB_SPACING / 2 + soleBreadcrumbOffset,
        y:
          -this.hub.centerY +
          BREADCRUMB_SPACING / 2 +
          BREADCRUMB_SPACING * depth +
          soleBreadcrumbOffset,
      },
      animationMs,
    );
    this.applyRestingScale(animationMs);
  }

  /**
   * Apply final target values for any interrupted tweens.
   * Called after stopAllTweensAndDelays() to ensure correct display state.
   */
  public finalizeTweens(): void {
    this.button.applyTargetScale();
    if (this._targetPosition) {
      this.x = this._targetPosition.x;
      this.y = this._targetPosition.y;
    }
  }

  public hide({ animationMs }: { animationMs: number }): void {
    logger.forMethod(this.hide).trace(this.traceData);
    this.position = null;
    this.animateHide(animationMs);
    this.animateCollapse(animationMs);
  }

  public setLabel(label: Label | TextLabel): void {
    this.button.setLabel(label);
  }

  public setInfoPane(infoPane: InfoPane | null): void {
    this.infoPane = infoPane;
  }

  private showKeyboardFocus(): void {
    if (this.position === 'breadcrumb') {
      this.button.setScale(
        this.computeRestingScale() * KEYBOARD_FOCUS_SCALE_FACTOR,
        this.animationMs,
      );
      return;
    }

    this.onHover();
  }

  private hideKeyboardFocus(): void {
    if (this.position === 'breadcrumb') {
      this.applyRestingScale(this.animationMs);
      return;
    }

    this.onLeave();
  }

  private onHover(): void {
    const button = this.button;
    const rad = button.parent;

    if (this.position === 'breadcrumb') return;
    if (this.position === 'center' && !this.radialParent) return;

    const isCentral = this.position === 'center';
    this.hub.setChildIndex(rad, this.hub.numChildren - (isCentral ? 1 : 2));
    button.onHover({
      animationMs: this.animationMs,
      isCentral,
      inFocus: this.inFocus,
    });
  }

  private onLeave(): void {
    if (this.position === 'breadcrumb') return;
    this.button.onLeave({
      animationMs: this.animationMs,
      isCentral: this.position === 'center',
    });
  }

  private onClick(event: ActivationEvent): void {
    const fromAccessibility = !event.nativeEvent;
    if (
      fromAccessibility &&
      (!this.display ||
        !this.enabled ||
        !this.inFocus ||
        this.underConstruction)
    ) {
      return;
    }

    const button = this.button;
    const rad = button.parent as Radial;

    if (this.position === 'center' && !this.radialParent) return;
    if (rad.redirect) this.hub.clickRadial(rad.redirect);
    else this.hub.clickRadial(rad);
  }

  private animatePosition(
    { x, y }: { x: number; y: number },
    animationMs: number,
  ): void {
    this._targetPosition = { x, y };
    UIUtil.tween(this, 'x', x, animationMs, 'easeInOut');
    UIUtil.tween(this, 'y', y, animationMs, 'easeInOut');
  }

  private animateShow(animationMs: number): void {
    UIUtil.tween(
      this,
      'alpha',
      SHOWN_ALPHA,
      animationMs * ALPHA_ANIMATION_FACTOR,
      'easeIn',
    );
  }

  private animateHide(animationMs: number): void {
    UIUtil.tween(
      this,
      'alpha',
      HIDDEN_ALPHA,
      animationMs * ALPHA_ANIMATION_FACTOR,
      'strongOut',
    );
  }

  private animateExpand(animationMs: number): void {
    const button = this.button;
    UIUtil.tween(
      this,
      'rotation',
      this.angle,
      animationMs * EMERGE_ROTATION_ANIMATION_SCALE_FACTOR,
      ROTATION_EASE,
    );
    UIUtil.tween(
      button,
      'rotation',
      -this.angle,
      animationMs * EMERGE_ROTATION_ANIMATION_SCALE_FACTOR,
      ROTATION_EASE,
    );
    UIUtil.tween(
      button,
      'x',
      -this.stemLength * (this.emphasized ? EMPHASIZED_RADIUS_FACTOR : 1),
      animationMs * ELASTIC_ANIMATION_FACTOR,
      ELASTIC_EASE,
    );
    UIUtil.tween(
      this.stem,
      'alpha',
      this.emphasized ? EMPHASIZED_STEM_ALPHA : STEM_ALPHA,
      animationMs,
      'strongOut',
    );
    UIUtil.tween(
      this.stem,
      'scaleX',
      1,
      animationMs * ELASTIC_ANIMATION_FACTOR,
      ELASTIC_EASE,
    );
    UIUtil.tween(
      this.stem,
      'scaleY',
      this.emphasized ? EMPHASIZED_STEM_WIDTH_FACTOR : 1,
      animationMs * ELASTIC_ANIMATION_FACTOR,
      ELASTIC_EASE,
    );
  }

  private animateCollapse(animationMs: number): void {
    const button = this.button;
    UIUtil.tween(button, 'x', 0, animationMs, 'easeInOut');
    UIUtil.tween(
      this,
      'rotation',
      0,
      animationMs * EMERGE_ROTATION_ANIMATION_SCALE_FACTOR,
      ROTATION_EASE,
    );
    UIUtil.tween(
      button,
      'rotation',
      0,
      animationMs * EMERGE_ROTATION_ANIMATION_SCALE_FACTOR,
      ROTATION_EASE,
    );
    UIUtil.tween(this.stem, 'scaleX', 0, animationMs, 'easeInOut');
    UIUtil.tween(
      this.stem,
      'scaleY',
      EMERGE_STEM_WIDTH_FACTOR,
      animationMs,
      'easeInOut',
    );
    UIUtil.tween(this.stem, 'alpha', HIDDEN_ALPHA, animationMs, 'strongOut');
  }
}
